import * as Dialog from '@radix-ui/react-dialog';
import { type ReactNode } from 'react';

/**
 * Destructive-confirm dialog. Built on @radix-ui/react-dialog.
 *
 * oscar/CLAUDE.md forbids `window.confirm` — use this wrapper instead for
 * any action that's destructive or hard to undo (soft-deleting a record,
 * disposing a device, dispatching emails, etc.).
 *
 * Usage:
 *   <AlertDialog
 *     title="Delete this person?"
 *     description="The record is soft-deleted and hidden from registers."
 *     confirmLabel="Delete"
 *     destructive
 *     onConfirm={() => mutation.mutate()}
 *     open={open}
 *     onOpenChange={setOpen}
 *   />
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  hideCancel = false,
  destructive = false,
  closeOnConfirm = true,
  confirmDisabled = false,
  wide = false,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * ⛔ OPT-IN, so widening one form does not widen every dialog in Oscar. The
   * default 460px is right for a confirm and two fields; it is not right for a
   * form with side-by-side columns, where the middle box ends up a few
   * characters wide and the text scrolls inside it.
   *
   * ⚠ Still bounded by the viewport (94vw), so a phone is unaffected.
   */
  wide?: boolean;
  /**
   * ⚠ For a READ-ONLY dialog. Without it such a dialog shows both Cancel and
   * Close — two buttons that do the same thing, one of them implying the
   * reader is abandoning a change they never made.
   *
   * ⛔ Never set this on a dialog that confirms something. Taking the way out
   * off a destructive confirm leaves Escape as the only escape.
   */
  hideCancel?: boolean;
  destructive?: boolean;
  /** When false, confirming runs onConfirm but leaves the dialog open so the
   *  caller can show a validation/API error and close it itself on success. */
  closeOnConfirm?: boolean;
  /** Greys out the confirm button. Additive and optional — a dialog that
   *  does not pass it behaves exactly as before. */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-50" />
        <Dialog.Content
          className={`fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-5 shadow-xl ring-1 ring-black/5 focus:outline-none ${wide ? 'w-[min(94vw,860px)]' : 'w-[min(92vw,460px)]'}`}
          aria-describedby={description ? 'alert-dialog-desc' : undefined}
        >
          <Dialog.Title className="font-heading text-lg font-bold text-navy">
            {title}
          </Dialog.Title>
          {description && (
            <Dialog.Description id="alert-dialog-desc" className="mt-2 text-sm text-darkGrey">
              {description}
            </Dialog.Description>
          )}
          {children && <div className="mt-3">{children}</div>}
          <div className="mt-5 flex justify-end gap-2">
            {!hideCancel && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-navy"
                >
                  {cancelLabel}
                </button>
              </Dialog.Close>
            )}
            <button
              type="button"
              disabled={confirmDisabled}
              onClick={() => {
                onConfirm();
                if (closeOnConfirm) onOpenChange(false);
              }}
              className={
                confirmDisabled
                  ? 'rounded bg-darkGrey/30 px-3 py-1.5 text-sm font-semibold text-white cursor-not-allowed'
                  : destructive
                    ? 'rounded bg-red px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-red'
                    : 'rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange'
              }
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
